import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { TONGAN_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://images.unsplash.com/photo-1603668103905-3ef7c99d7f9f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function TonganChurchUniforms() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Tongan Church Uniforms",
    serviceType: "Custom Tongan church polo uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Tongan Church Uniforms — custom ngatu pattern Pacific Polo Shirts for Siasi Uesiliana Auckland NZ",
    name: "Tongan Church Uniforms — Heritage Polo",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Tongan Church Uniforms NZ | Custom Polo Shirts"
        description="Tongan Church Uniforms in NZ — custom Pacific polo shirts for Free Wesleyan, Catholic, AOG & Methodist Tongan congregations. Ngatu-inspired designs. Free mockup in 24h."
        path="/tongan-church-uniforms"
        image={HERO_IMG}
        imageAlt="Tongan Church Uniforms — custom Pacific polo shirts Auckland NZ"
        keywords={joinKw(TONGAN_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="Siasi Uesiliana · Catholic · AOG · Mormon"
        heroH1="Tongan Church Uniforms — Custom Polo Shirts for Your Siasi"
        heroSub="Authentic Tongan church polo uniforms designed in Auckland and delivered NZ-wide. For ushers, choirs (hiva kakala), women's and men's fellowship, youth and conference teams."
        heroImage={HERO_IMG}
        heroImageAlt="Custom Tongan church polo uniforms with ngatu pattern Auckland NZ"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Tongan Church Uniforms" }]}
      >
        <h2>For Tongan churches across Aotearoa</h2>
        <p>
          Tongan churches are at the heart of every Tongan community in New
          Zealand — Sunday services, fakamē, conference week, youth camps,
          choir performances at every major event. The Tongan church
          showing up with coordinated <strong>custom Tongan church
          uniforms</strong> tells visitors and members alike that this is a
          place of order, unity and pride. We design polo uniforms for
          Tongan congregations of every tradition: Free Wesleyan (Siasi
          Uesiliana Tau'atāina), Catholic, AOG, LDS, Methodist, and
          independent Tongan-language ministries.
        </p>
        <p>
          Every uniform we design begins with your group's identity. Your
          church name, your minister, your colours, your village heritage —
          all woven into a polo that your members will want to wear every
          chance they get.
        </p>

        <h2>Groups we outfit in Tongan congregations</h2>
        <ul>
          <li><strong>Choirs (hiva kakala)</strong> — Sunday choir, youth choir, women's choir</li>
          <li><strong>Ushers / welcoming teams</strong></li>
          <li><strong>Women's fellowship (Kau Fefine)</strong></li>
          <li><strong>Men's fellowship (Kau Tangata)</strong></li>
          <li><strong>Youth ministry &amp; youth conferences</strong></li>
          <li><strong>Sunday school teachers</strong></li>
          <li><strong>Stewards, deacons &amp; leadership teams</strong></li>
          <li><strong>Annual conference (Conference Kakai) committees</strong></li>
        </ul>

        <h2>Tongan cultural design elements we work with</h2>
        <p>
          Our designers integrate Tongan symbols and motifs into modern polo
          shirt designs in ways that respect their meaning. Common requests
          include:
        </p>
        <ul>
          <li><strong>Ngatu (tapa) patterns</strong> — as panel prints, sleeve detail, or subtle background motifs</li>
          <li><strong>Kupesi designs</strong> — traditional Tongan geometric line work</li>
          <li>Village (kolo) name and crest</li>
          <li>Church name in Tongan + English</li>
          <li>Minister and church-leader name embroidery</li>
          <li>Kahoa (lei), tikis and other meaningful Pacific iconography</li>
          <li>Year and event embroidery for conferences, camps, fakamē</li>
        </ul>

        <h2>Fabric, fit &amp; finish</h2>
        <p>
          Our standard polo blank is a mid-weight cotton/poly blend that
          breathes well for NZ summers, holds its shape after multiple
          washes, and takes embroidery and full-colour print beautifully.
          We carry sizes from XS through 7XL — important for families and
          church groups where every uncle, aunty and matua needs a fit
          that works.
        </p>
        <p>
          For formal occasions like Conference Kakai or fakamē, we can also
          produce button-front polos and matching ie faitaga / tupenu
          accents with embroidered church crests.
        </p>

        <h2>Order timeline</h2>
        <p>
          Plan 4–6 weeks ahead of major events (Conference Kakai, youth
          camps, fakamē) to allow time for the design process, committee
          approval, sizing and production. For urgent jobs we can offer
          expedited turnaround — get in touch early to lock in capacity.
        </p>

        <h2>Pricing</h2>
        <p>
          Our bulk polo pricing starts from <strong>$59 per polo</strong>{" "}
          (10–24 units) and scales down to <strong>$49 per polo</strong> at
          50+ units. For very large conference or multi-village orders
          (100+), we provide custom quotes. See our <a href="/#pricing">
          bulk pricing</a> for full details.
        </p>

        <h2>Related collections</h2>
        <p>
          Also designing for <Link to="/samoan-church-uniforms">Samoan church
          uniforms</Link>, <Link to="/cook-islands-uniforms">Cook Islands
          uniforms</Link>, and <Link to="/church-conference-uniforms">
          church conference uniforms</Link>. All from one Auckland-based
          Pasifika studio.
        </p>
      </SEOPageLayout>
    </>
  );
}
